import { defineStore } from 'pinia'
import { store } from '../index'
import { enableStoreHMRUpdate } from '../helper'
import { auth } from '@/utils'
import type { LoginParams, UserInfoModel } from '@/api/common/model/userModel'
import * as userApi from '@/api/common/user'
import { router } from '@/router'
import { PageEnum } from '@/enums/pageEnum'
import { usePermissionStoreWithOut } from './permission'
import { useLocaleStoreWithOut } from '@/store-pinia/modules/locale'
import {
  setWarehouseSavingTime,
  setWarehouseTimezone,
  getOrignTimezoneType,
  setTimezoneType,
  TimezoneType,
} from '@/utils/timezoneCache'
import { useWarehouseSwitch } from '@/composables/useWarehouseSwitch'

export interface State {
  token?: string
  userInfo: Nullable<UserInfoModel>
  roleList: string[]
  lastUpdateTime: number
}

export const useUserStore = defineStore('userStore', {
  state: (): State => ({
    // 用户信息
    userInfo: null,
    // 登录凭证
    token: auth.get() || undefined,
    // 上次更新时间
    lastUpdateTime: 0,
    // 角色列表
    roleList: [],
  }),
  getters: {
    getUserInfo(): UserInfoModel | null {
      return this.userInfo
    },
    getToken(): string | undefined {
      return this.token
    },
    getRoleList(): string[] {
      return this.roleList.length > 0 ? this.roleList : []
    },
    getLastUpdateTime(): number {
      return this.lastUpdateTime
    },
  },
  actions: {
    /**
     * 设置登录凭证token
     * @param token
     */
    setToken(token: string | undefined) {
      this.token = token
      if (token) {
        auth.set(token)
      } else {
        auth.clear()
      }
    },
    /**
     * 设置角色列表
     * @param roleList
     */
    setRoleList(roleList: string[]) {
      this.roleList = roleList
    },
    /**
     * 设置用户信息
     * @param info
     */
    setUserInfo(info: UserInfoModel) {
      this.userInfo = info
      this.lastUpdateTime = new Date().getTime()
      window.localStorage.setItem('user_code', info.userCode)
      window.localStorage.setItem('user_name', info.username)
      //设置用户属性
      // @ts-ignore
      window.gtag('set', 'user_properties', {
        customer_user: info.userCode, //客 户代码_用户ID
      })
      setWarehouseTimezone(info.warehouseTimeZone)
      setWarehouseSavingTime(info.warehouseSavingTime)
      if (getOrignTimezoneType() == null) {
        setTimezoneType(TimezoneType.WH)
      }
    },
    /**
     * 重置state
     */
    resetState() {
      this.$reset()
      this.token = undefined
    },

    /**
     * 登录
     * @param params
     * @returns
     */
    async login(params: LoginParams & { goHome?: boolean }): Promise<UserInfoModel | null> {
      try {
        const { goHome = true, ...loginParams } = params
        const data = await userApi.login(loginParams)
        const { owmsToken } = data
        this.setToken(owmsToken)
        return this.afterLogin(goHome)
      } catch (error) {
        return Promise.reject(error)
      }
    },

    /**
     * fbg自动登录
     */
    async autoLogin(
      authCode: string,
      regionWarehouseCode: string,
      goHome = true,
    ): Promise<UserInfoModel | null> {
      try {
        const data = await userApi.autoLogin({ authCode, regionWarehouseCode })
        const { owmsToken } = data
        this.setToken(owmsToken)
        return this.afterLogin(goHome)
      } catch (error) {
        return Promise.reject(error)
      }
    },

    /**
     * 登录之后操作
     * @param goHome
     * @returns
     */
    async afterLogin(goHome?: boolean): Promise<UserInfoModel | null> {
      const userInfo: UserInfoModel = await this.loadUserInfo()
      const permissionStore = usePermissionStoreWithOut()
      if (!permissionStore.isDynamicAddedRoute) {
        const routes = await permissionStore.buildRoutes()
        routes.forEach((route) => {
          router.addRoute(route)
        })
        permissionStore.setIsDynamicAddedRoute(true)
      }
      goHome && (await router.replace(PageEnum.BASE_HOME))
      return userInfo
    },
    /**
     * 加载用户信息
     * @returns
     */
    async loadUserInfo(): Promise<UserInfoModel> {
      const userInfo = await userApi.getUserInfo()
      const { userRoles = [] } = userInfo
      if (Array.isArray(userRoles)) {
        this.setRoleList(userRoles)
      } else {
        userInfo.userRoles = []
        this.setRoleList([])
      }
      const { isChina } = useLocaleStoreWithOut()
      if (userInfo.physicsWarehouseList) {
        userInfo.physicsWarehouseList.forEach((item) => {
          item.physicsWarehouseName =
            (isChina ? item.physicsWarehouseNameCn : item.physicsWarehouseNameEn) || 'unknown'
        })
      }
      this.setUserInfo(userInfo)
      return userInfo
    },
    /**
     * 注销登录
     */
    async logout(goLogin = true) {
      const { clearWarehouseChoseCache, setWarehouseHasChoseCache, isRememberChoiceRef } =
        useWarehouseSwitch(this.getUserInfo?.userCode!)
      if (this.token) {
        try {
          await userApi.logout()
        } catch {
          console.log('登出失败, token未被注销')
        }
      }
      if (!isRememberChoiceRef.value) {
        setWarehouseHasChoseCache('')
        clearWarehouseChoseCache()
      }

      this.setToken(undefined)
      if (goLogin && !router.currentRoute.value.fullPath.includes(PageEnum.BASE_LOGIN)) {
        location.assign(`${PageEnum.BASE_LOGIN}?redirect=${router.currentRoute.value.fullPath}`)
      }
    },
  },
})

enableStoreHMRUpdate(useUserStore)

export function useUserStoreWithOut() {
  return useUserStore(store)
}
